"use client"

import { useState, useEffect, Fragment } from "react"
import { Dialog, Transition } from "@headlessui/react"
import {
  X,
  FloppyDisk,
  ArrowClockwise,
  WarningCircle,
  UploadSimple,
  FileText,
  Image,
  ChatCircleDots,
} from "phosphor-react"
import { InputCustom, SearchableSelect } from "../../components"
import ticketsService from "../../services/ticketsService"
import adAccountsService from "../../services/adAccountsService"
import toast from "react-hot-toast"

function CreateTicketModal({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [adAccountOptions, setAdAccountOptions] = useState([])
  const [selectedFiles, setSelectedFiles] = useState([])

  const [ticketDetails, setTicketDetails] = useState({
    title: "",
    reason: "",
    other_reason: "",
    priority: "medium",
    description: "",
    ad_account_id: "",
    attachments: [],
  })

  const [errors, setErrors] = useState({
    title: "",
    reason: "",
    other_reason: "",
    description: "",
    ad_account_id: "",
  })

  const reasonOptions = [
    { id: "account_suspended", value: "account_suspended", name: "Account Suspended", icon: "🚫" },
    { id: "payment_issue", value: "payment_issue", name: "Payment Issue", icon: "💳" },
    { id: "ad_rejected", value: "ad_rejected", name: "Ad Rejected", icon: "❌" },
    { id: "billing_problem", value: "billing_problem", name: "Billing Problem", icon: "💰" },
    { id: "technical_issue", value: "technical_issue", name: "Technical Issue", icon: "🔧" },
    { id: "account_access", value: "account_access", name: "Account Access", icon: "🔐" },
    { id: "policy_violation", value: "policy_violation", name: "Policy Violation", icon: "⚠️" },
    { id: "performance_issue", value: "performance_issue", name: "Performance Issue", icon: "📊" },
    { id: "other", value: "other", name: "Other", icon: "❓" },
  ]

  const priorityOptions = [
    {
      id: "low",
      value: "low",
      name: "Low",
      color: "bg-green-50 text-green-700",
      description: "Can wait",
    },
    {
      id: "medium",
      value: "medium",
      name: "Medium",
      color: "bg-yellow-50 text-yellow-700",
      description: "Normal priority",
    },
    {
      id: "high",
      value: "high",
      name: "High",
      color: "bg-orange-50 text-orange-700",
      description: "Needs attention",
    },
    {
      id: "urgent",
      value: "urgent",
      name: "Urgent",
      color: "bg-red-50 text-red-700",
      description: "Critical issue",
    },
  ]

  /* ---------------------- helpers ---------------------- */

  const convertFileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  const getFileIcon = (type) => {
    if (type?.startsWith("image/")) {
      return <Image className="h-4 w-4 text-blue-500" />
    }
    return <FileText className="h-4 w-4 text-neutral-500" />
  }

  /* ---------------------- fetch ad accounts ---------------------- */

  const fetchAdAccounts = async () => {
    try {
      setLoading(true)
      setLoadingOptions(true)

      const response = await adAccountsService.search({
        status: "active",
        pagination: {
          page: 1,
          limit: 100,
        },
      })

      if (response.data && response.data.statusCode === 200) {
        const accounts = response.data.data.map((acc) => ({
          id: acc._id,
          value: acc._id,
          name: acc.account_name || "Unknown Account",
        }))
        setAdAccountOptions(accounts)
      }
    } catch (error) {
      console.error("Failed to fetch ad accounts:", error)
      toast.error("Failed to load ad accounts")
    } finally {
      setLoading(false)
      setLoadingOptions(false)
    }
  }

  /* ---------------------- effects ---------------------- */

  useEffect(() => {
    if (!isOpen) return

    // reset form
    setTicketDetails({
      title: "",
      reason: "",
      other_reason: "",
      priority: "medium",
      description: "",
      ad_account_id: "",
      attachments: [],
    })
    setErrors({
      title: "",
      reason: "",
      other_reason: "",
      description: "",
      ad_account_id: "",
    })
    setSelectedFiles([])

    fetchAdAccounts()
  }, [isOpen])

  /* ---------------------- change handlers ---------------------- */

  const handleChange = (field, value) => {
    setTicketDetails((prev) => {
      let updated = { ...prev, [field]: value }

      // if reason changes and is not "other", clear custom reason
      if (field === "reason" && value !== "other") {
        updated.other_reason = ""
      }

      return updated
    })

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }))
    }
    if (field === "reason" && errors.other_reason) {
      setErrors((prev) => ({ ...prev, other_reason: "" }))
    }
  }

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || [])

    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ]
    const maxSize = 10 * 1024 * 1024 // 10MB

    const filtered = files.filter((file) => {
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name} is not a supported file type`)
        return false
      }
      if (file.size > maxSize) {
        toast.error(`${file.name} is larger than 10MB`)
        return false
      }
      return true
    })

    const merged = [...selectedFiles, ...filtered].slice(0, 5)
    setSelectedFiles(merged)

    try {
      const attachments = await Promise.all(
        merged.map(async (file) => ({
          name: file.name,
          type: file.type,
          size: file.size,
          base64: await convertFileToBase64(file),
        }))
      )
      setTicketDetails((prev) => ({ ...prev, attachments }))
    } catch (error) {
      console.error("Error converting files:", error)
      toast.error("Error processing files")
    }
  }

  const removeFile = async (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index)
    setSelectedFiles(newFiles)

    try {
      const attachments = await Promise.all(
        newFiles.map(async (file) => ({
          name: file.name,
          type: file.type,
          size: file.size,
          base64: await convertFileToBase64(file),
        }))
      )
      setTicketDetails((prev) => ({ ...prev, attachments }))
    } catch (error) {
      console.error("Error updating attachments:", error)
    }
  }

  /* ---------------------- validation ---------------------- */

  const validateForm = () => {
    const newErrors = {
      title: "",
      reason: "",
      other_reason: "",
      description: "",
      ad_account_id: "",
    }
    let isValid = true

    if (!ticketDetails.title.trim()) {
      newErrors.title = "Title is required"
      isValid = false
    }

    if (!ticketDetails.reason) {
      newErrors.reason = "Reason is required"
      isValid = false
    }

    if (ticketDetails.reason === "other" && !ticketDetails.other_reason.trim()) {
      newErrors.other_reason = "Please specify your reason"
      isValid = false
    }

    if (!ticketDetails.description.trim()) {
      newErrors.description = "Description is required"
      isValid = false
    }

    if (!ticketDetails.ad_account_id) {
      newErrors.ad_account_id = "Ad account is required"
      isValid = false
    }

    setErrors(newErrors)
    return isValid
  }

  /* ---------------------- submit ---------------------- */

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    try {
      setProcessing(true)

      const payload = {
        ...ticketDetails,
      }

      const response = await ticketsService.create(payload)

      if (response.data && response.data.statusCode === 200) {
        toast.success("Ticket created successfully")
        onSuccess && onSuccess()
        onClose && onClose()
      } else {
        throw new Error(response.data?.message || "Failed to create ticket")
      }
    } catch (error) {
      console.error("Ticket creation failed:", error)
      toast.error(error?.response?.data?.message || error.message || "Failed to create ticket")
    } finally {
      setProcessing(false)
    }
  }

  /* ---------------------- render ---------------------- */

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-xl transition-all">
                {/* header */}
                <div className="border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/40 p-5">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                      <ChatCircleDots className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
                      Create Support Ticket
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={onClose}
                      className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                {/* content */}
                <div className="p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <ArrowClockwise size={30} className="animate-spin text-neutral-600 dark:text-neutral-400" />
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* ad account */}
                        <div className="space-y-1 md:col-span-2">
                          <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
                            Ad Account <span className="text-red-500">*</span>
                          </label>
                          <SearchableSelect
                            label="Ad Account"
                            required
                            value={ticketDetails.ad_account_id}
                            onChange={(value) => handleChange("ad_account_id", value)}
                            options={adAccountOptions}
                            placeholder={loadingOptions ? "Loading accounts..." : "Select an ad account"}
                            disabled={loadingOptions}
                            error={errors.ad_account_id}
                          />
                        </div>

                        {/* title */}
                        <div className="space-y-1">
                          <InputCustom
                            label="Title"
                            required
                            value={ticketDetails.title}
                            onChange={(value) => handleChange("title", value)}
                            placeholder="Brief description of the issue"
                            error={errors.title}
                          />
                        </div>

                        {/* priority */}
                        <div className="space-y-1">
                          <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
                            Priority <span className="text-red-500">*</span>
                          </label>
                          <SearchableSelect
                            label="Priority"
                            required
                            value={ticketDetails.priority}
                            onChange={(value) => handleChange("priority", value)}
                            options={priorityOptions}
                            placeholder="Select priority"
                          />
                        </div>

                        {/* reason + other reason */}
                        <div className="space-y-1 md:col-span-2">
                          <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
                            Reason <span className="text-red-500">*</span>
                          </label>
                          <SearchableSelect
                            label="Reason"
                            required
                            value={ticketDetails.reason}
                            onChange={(value) => handleChange("reason", value)}
                            options={reasonOptions}
                            placeholder="Select a reason"
                            error={errors.reason}
                          />

                          {ticketDetails.reason === "other" && (
                            <div className="mt-3">
                              <InputCustom
                                label="Custom Reason"
                                required
                                value={ticketDetails.other_reason}
                                onChange={(value) => handleChange("other_reason", value)}
                                placeholder="Describe your reason..."
                                error={errors.other_reason}
                              />
                            </div>
                          )}
                        </div>

                        {/* description */}
                        <div className="space-y-1 md:col-span-2">
                          <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
                            Description <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            id="description"
                            name="description"
                            value={ticketDetails.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                            placeholder="Provide detailed information about your issue..."
                            rows={4}
                            className={`w-full text-neutral-800 dark:text-neutral-200 px-4 py-5 font-[300] text-sm dark:bg-neutral-900/30 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-600 focus:border-transparent ${
                              errors.description ? "border-red-500" : ""
                            }`}
                          />
                          {errors.description && (
                            <p className="mt-1 text-sm text-red-500">{errors.description}</p>
                          )}
                        </div>

                        {/* attachments */}
                        <div className="space-y-3 md:col-span-2">
                          <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
                            Attachments (Optional)
                          </label>
                          <div className="border-2 border-dashed border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl p-6 text-center hover:border-neutral-300 dark:hover:border-neutral-700 transition-colors">
                            <input
                              type="file"
                              id="file-upload"
                              multiple
                              accept="image/*,.pdf,.doc,.docx,.txt"
                              onChange={handleFileSelect}
                              className="hidden"
                              disabled={selectedFiles.length >= 5}
                            />
                            <label htmlFor="file-upload" className="cursor-pointer">
                              <div className="p-2 bg-neutral-50 dark:bg-neutral-900/40 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center border border-neutral-200/80 dark:border-neutral-800">
                                <UploadSimple className="h-6 w-6 text-neutral-400" />
                              </div>
                              <p className="text-sm text-neutral-600 dark:text-neutral-400 font-medium">
                                Click to upload files or drag and drop
                              </p>
                              <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-1">
                                Images, PDF, DOC, TXT (max 10MB each, up to 5 files)
                              </p>
                            </label>
                          </div>

                          {selectedFiles.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                                Selected Files:
                              </p>
                              {selectedFiles.map((file, index) => (
                                <div
                                  key={index}
                                  className="border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl p-3"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {getFileIcon(file.type)}
                                      <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                                          {file.name}
                                        </p>
                                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                          {formatFileSize(file.size)}
                                        </p>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removeFile(index)}
                                      className="text-neutral-400 hover:text-red-500 dark:text-neutral-500 dark:hover:text-red-400"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* info box */}
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/40">
                        <div className="flex items-center gap-2 mb-1">
                          <WarningCircle size={16} className="text-blue-600 dark:text-blue-400" />
                          <h3 className="text-sm font-medium text-blue-700 dark:text-blue-300">
                            What happens next?
                          </h3>
                        </div>
                        <ul className="text-xs text-blue-600 dark:text-blue-300 space-y-1">
                          <li>• Your ticket will be assigned to your account manager</li>
                          <li>• You&apos;ll receive updates via the platform and email</li>
                          <li>• Response time: 2–24 hours depending on priority</li>
                        </ul>
                      </div>
                    </form>
                  )}
                </div>

                {/* footer */}
                <div className="border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/40 p-5 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-xl hover:bg-neutral-50 dark:bg-transparent dark:text-neutral-300 dark:border-neutral-600 dark:hover:bg-neutral-800 focus:outline-none"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={processing || loading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white dark:text-neutral-900 bg-neutral-900 dark:bg-neutral-100 dark:hover:bg-neutral-300 rounded-xl hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {processing ? (
                      <>
                        <ArrowClockwise className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <FloppyDisk className="mr-2 h-4 w-4" />
                        Create Ticket
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default CreateTicketModal
